/**
 * financialReportFreshness — is a Financial Viability report still describing
 * the model it was generated from?
 *
 * WHY THIS EXISTS. The Financial report's verdict is a snapshot: the engine runs
 * once at generation and its output is stamped onto `report.financialAnalysis`.
 * Nothing re-runs it. So editing the CAPEX, the PPA price or the WACC leaves the
 * report — and the cover chips, the dashboard verdict, the Go/No-Go rollup and
 * every export — showing a band computed from numbers the site no longer holds,
 * with no signal that anything moved.
 *
 * The build spec called for a recompute on financial-assumptions change and it
 * was never implemented; the only staleness anyone could see came from the
 * supply-chain and pathway-comparison banners, neither of which watches the FA.
 *
 * This module is the DETECTION half. The report is compared against the three
 * inputs its economics actually depend on:
 *   - the financial assumptions themselves (the model),
 *   - the project intent (phase capacities and CODs drive the phased engine),
 *   - the supply-chain plan (per-phase BoM CAPEX is the authoritative basis).
 *
 * Pure. No I/O, no store reads.
 */
import type { SiteEntity } from '../types/site';
import type { ProjectIntent, SiteRagReport } from '../types/siteRag';

/** Which input moved after the report was generated. */
export type FinancialDriftSource = 'financial-model' | 'project-intent' | 'supply-chain';

export interface FinancialReportFreshness {
  stale: boolean;
  /** Every input that changed since generation, newest-first is not implied. */
  changed: FinancialDriftSource[];
  /** ISO timestamp the report's financial snapshot was taken. */
  generatedAtLabel?: string;
  /** ISO timestamp of the most recent input change. */
  changedAtLabel?: string;
  /** Whole days between the two, floored at 1 when they differ at all. */
  staleDays?: number;
  /**
   * True when the report's FIGURES have already been recomputed against the
   * changed model (`financialAnalysis.restampedAt` is at or after every
   * timestamped change, and no structural intent drift is outstanding). Only
   * the written analysis is then stale, and the banner must say so — telling a
   * reader that current numbers are stale is its own untruth.
   *
   * False whenever anything is outstanding, including the case where no
   * re-stamp has run at all.
   */
  figuresCurrent: boolean;
}

function toEpoch(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Human label for a drift source, for the banner copy. */
export function financialDriftSourceLabel(source: FinancialDriftSource): string {
  switch (source) {
    case 'financial-model': return 'the financial model';
    case 'project-intent': return 'the project phases';
    case 'supply-chain': return 'the equipment plan';
  }
}

/**
 * Compare a Financial report's gen-time snapshot against the live inputs.
 *
 * Returns `{ stale: false }` when the report is not financial, when it carries
 * no `financialAssumptionsAtGen` (nothing to compare — an older report, or one
 * generated before the snapshot existed), or when every input is at or behind
 * the snapshot. Never guesses: an input with no timestamp is simply not
 * evidence of drift.
 */
export function getFinancialReportFreshness(
  report: SiteRagReport | undefined,
  site: Pick<SiteEntity, 'financialAssumptions' | 'projectIntent' | 'supplyChainPlan'> | null | undefined,
): FinancialReportFreshness {
  if (!report || report.reportType !== 'financial') return { stale: false, changed: [], figuresCurrent: true };

  // The snapshot the report was generated against. Prefer the FA snapshot's own
  // timestamp; fall back to the stamp's `computedAt`, which is written by the
  // same pass.
  const generatedAt = toEpoch(report.financialAssumptionsAtGen?.updatedAt)
    ?? toEpoch(report.financialAnalysis?.computedAt);
  if (generatedAt == null) return { stale: false, changed: [], figuresCurrent: true };

  const candidates: Array<[FinancialDriftSource, number | null]> = [
    ['financial-model', toEpoch(site?.financialAssumptions?.updatedAt)],
    ['supply-chain', toEpoch(site?.supplyChainPlan?.updatedAt)],
  ];

  const changed: FinancialDriftSource[] = [];
  let latest: number | null = null;
  // A re-stamp advances the figures without touching the narrative. Compare it
  // against the inputs, NOT against `generatedAt`.
  const restampedAt = toEpoch(report.financialAnalysis?.restampedAt);
  let figuresBehind = false;
  for (const [source, at] of candidates) {
    if (at == null || at <= generatedAt) continue;
    changed.push(source);
    if (latest == null || at > latest) latest = at;
    if (restampedAt == null || restampedAt < at) figuresBehind = true;
  }

  // PROJECT INTENT has no `updatedAt`, so its drift is detected STRUCTURALLY —
  // by comparing the phase fields the engine actually consumes against the
  // gen-time snapshot. That is more precise than a timestamp anyway: renaming a
  // phase does not change any number, but re-sequencing one or moving a COD
  // does. When there is no snapshot to compare against, no claim is made.
  if (report.projectIntentAtGen && site?.projectIntent
    && phaseEconomicsFingerprint(report.projectIntentAtGen) !== phaseEconomicsFingerprint(site.projectIntent)) {
    changed.push('project-intent');
    // Intent drift is STRUCTURAL, so there is no timestamp to compare a
    // re-stamp against — and the re-stamp only runs on financial-assumptions
    // saves anyway. Never claim the figures are current while it is outstanding.
    figuresBehind = true;
  }

  if (changed.length === 0) return { stale: false, changed: [], figuresCurrent: true };

  return {
    stale: true,
    changed,
    figuresCurrent: restampedAt != null && !figuresBehind,
    generatedAtLabel: new Date(generatedAt).toISOString(),
    ...(latest != null ? { changedAtLabel: new Date(latest).toISOString() } : {}),
    ...(latest != null ? { staleDays: Math.max(1, Math.ceil((latest - generatedAt) / MS_PER_DAY)) } : {}),
  };
}

/**
 * The intent fields the financial engines read, as a comparable string: each
 * phase's id, order, capacity and COD. Deliberately NOT the whole intent —
 * a label edit or a note must not raise a staleness banner on a report whose
 * numbers cannot have moved.
 */
function phaseEconomicsFingerprint(intent: ProjectIntent): string {
  const phases = intent.phases ?? [];
  return phases
    .map((p) => [p.id, p.sequence, p.capacityMW ?? '', p.targetInServiceDate ?? ''].join(':'))
    .sort()
    .join('|');
}
